
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { format, parseISO } from "date-fns";
import Hey from "@/lib/hey-utils";
import { Trip, TripElement, FlightDetails } from "@/types/trip";
import { trackTripCardAction } from "@/utils/analytics";

export const useShareAndActions = (
  trip: Trip, 
  getDestinationName: () => string, 
  sortedElements: TripElement[]
) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleShare = async (method: 'native' | 'whatsapp' | 'email' | 'sms') => {
    const tripDetails = `
${trip.title || getDestinationName()}
${t("tripCard.dates")}: ${trip.dates}
${trip.tripType ? `${t("tripCard.type")}: ${trip.tripType}` : ''}
${trip.numberOfTravelers ? `${t("tripCard.travelers")}: ${trip.numberOfTravelers}` : ''}
    `.trim();
    try {
      switch (method) {
        case 'native':
          if (navigator.share) {
            await navigator.share({
              title: trip.title || getDestinationName(),
              text: tripDetails
            });
            toast({
              description: t("tripCard.sharedSuccessfully")
            });
          }
          break;
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(tripDetails)}`, '_blank');
          break;
        case 'email':
          window.location.href = `mailto:?subject=${encodeURIComponent(trip.title || getDestinationName())}&body=${encodeURIComponent(tripDetails)}`;
          break;
        case 'sms':
          window.location.href = `sms:?body=${encodeURIComponent(tripDetails)}`;
          break;
      }
    } catch (error) {
      console.error('Error sharing trip:', error);
      toast({
        variant: "destructive",
        description: t("tripCard.shareError")
      });
    }
  };

  const handleDeleteTrip = async () => {
    if (!trip.id) {
      console.error('No trip ID provided for deletion');
      toast({
        variant: "destructive",
        description: "Error: Cannot delete trip without ID"
      });
      return;
    }
    try {
      let isDeleted = Hey.HeyUtils.getInstance().removeTrip(trip.id);
      if (!isDeleted) {
        throw new Error('Unable to delete trip with id: ' + trip.id);
      }
      toast({
        description: t("tripCard.deletedSuccessfully")
      });
      await queryClient.invalidateQueries({
        queryKey: ['trips']
      });
      return true;
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast({
        variant: "destructive",
        description: t("tripCard.deleteError")
      });
      return false;
    }
  };

  // New implementation leveraging the same approach used in HotelSegment component
  const handleAddToCalendar = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation(); // Prevent event from bubbling up
    
    const firstFlight = sortedElements.find(element => element.type === 'flight');
    if (!firstFlight) return;
    
    const flightDetails = firstFlight.details as FlightDetails;
    const startDate = parseISO(flightDetails.departure.time || "1970");
    const endDate = parseISO(flightDetails.arrival.time || "1970");
    
    const title = `Flight: ${flightDetails.airline} ${flightDetails.flightNumber}`;
    const description = `From: ${flightDetails.departure.code} (${flightDetails.departure.city})
To: ${flightDetails.arrival.code} (${flightDetails.arrival.city})
Airline: ${flightDetails.airline}
Flight Number: ${flightDetails.flightNumber}`;
    
    const isAppleDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      console.debug("Calendar - User agent:", userAgent);
      const isApple = /iphone|ipad|ipod|mac|macintosh/.test(userAgent);
      console.debug("Calendar - Is Apple device:", isApple);
      return isApple;
    };
    
    // Track the action
    trackTripCardAction("add_to_calendar", trip.id, getDestinationName());
    
    if (isAppleDevice()) {
      openAppleCalendar(title, description, startDate, endDate, flightDetails.departure.code);
    } else {
      openGoogleCalendar(title, description, startDate, endDate, flightDetails.departure.code);
    }
  };
  
  const openAppleCalendar = (title: string, description: string, startDate: Date, endDate: Date, location: string) => {
    // Format dates for Apple Calendar URL
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();
    
    // Create Apple Calendar URL with all parameters (webcal protocol)
    const calendarUrl = `webcal://p30-calendarws.icloud.com/ca/subscribe/1/${encodeURIComponent(title)}?${
      new URLSearchParams({
        title: encodeURIComponent(title),
        desc: encodeURIComponent(description),
        location: encodeURIComponent(location),
        dtstart: formattedStartDate,
        dtend: formattedEndDate
      }).toString()
    }`;
    
    console.debug("Calendar - Apple Calendar URL:", calendarUrl);
    
    // Open calendar URL
    window.location.href = calendarUrl;
  };

  const openGoogleCalendar = (title: string, description: string, startDate: Date, endDate: Date, location: string) => {
    // Format dates for calendar URL (remove special characters)
    const formattedStartDate = startDate.toISOString().replace(/-|:|\.\d+/g, '');
    const formattedEndDate = endDate.toISOString().replace(/-|:|\.\d+/g, '');
    
    // Create calendar URL with all parameters
    const calendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formattedStartDate}/${formattedEndDate}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}&sprop=&sprop=name:`;
    
    console.debug("Calendar - Google Calendar URL:", calendarUrl);
    
    // Open calendar URL
    window.open(calendarUrl, '_blank');
  };

  return {
    handleShare,
    handleDeleteTrip,
    handleAddToCalendar
  };
};
